// Desk shared API contract. Single source of truth for the API, web/Electron
// renderer, MCP server, and (via openapi.json) the Swift client.
//
// Conventions
// - All authenticated routes live under /v1 and require `Authorization: Bearer <token>`.
// - Timestamps are ISO-8601 UTC strings. Day-only values (deadline, plan dates)
//   are "YYYY-MM-DD" local calendar dates and are never converted through UTC.
// - Every mutable record carries `revision` (integer, starts at 1). Mutations may
//   send `baseRevision`; a mismatch returns 409 with the current record.
// - Creates accept an `Idempotency-Key` header and/or `clientId` (UUID). Repeating a
//   create with the same key returns the original record (200) instead of a duplicate.
// - Errors use the ApiError shape with a stable `code`.
import { z } from "zod";

// ---------- primitives ----------
export const Uuid = z.string().uuid();
export const IsoDateTime = z.string().datetime({ offset: true });
/** True for a real calendar date (rejects 2026-02-30, 2026-13-01). */
export function isCalendarDate(value: string): boolean {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  if (!m) return false;
  const [y, mo, d] = [Number(m[1]), Number(m[2]), Number(m[3])];
  const dt = new Date(Date.UTC(y, mo - 1, d));
  return dt.getUTCFullYear() === y && dt.getUTCMonth() === mo - 1 && dt.getUTCDate() === d;
}
export const LocalDate = z
  .string()
  .regex(/^\d{4}-\d{2}-\d{2}$/, "Expected YYYY-MM-DD")
  .refine(isCalendarDate, "Not a real calendar date");
export const Prefix = z.string().regex(/^[A-Z][A-Z0-9]{1,5}$/, "2-6 uppercase letters/digits");
export const ReadableId = z.string().regex(/^[A-Z][A-Z0-9]{1,5}-\d+$/);

export const TaskStatus = z.enum(["backlog", "todo", "in_progress", "done", "cancelled"]);
export type TaskStatus = z.infer<typeof TaskStatus>;
export const TASK_STATUSES = TaskStatus.options;
export const OPEN_STATUSES: TaskStatus[] = ["backlog", "todo", "in_progress"];
export const STATUS_LABEL: Record<TaskStatus, string> = {
  backlog: "Backlog",
  todo: "To do",
  in_progress: "In progress",
  done: "Done",
  cancelled: "Cancelled",
};

export const Priority = z.enum(["none", "low", "medium", "high", "urgent"]);
export type Priority = z.infer<typeof Priority>;
export const PRIORITY_LABEL: Record<Priority, string> = {
  none: "No priority",
  low: "Low",
  medium: "Medium",
  high: "High",
  urgent: "Urgent",
};
/** Sort rank: lower sorts first. Urgent, High, Medium, Low, No priority. */
export const PRIORITY_RANK: Record<Priority, number> = { urgent: 0, high: 1, medium: 2, low: 3, none: 4 };

export const ProjectState = z.enum(["planned", "active", "completed", "archived"]);
export type ProjectState = z.infer<typeof ProjectState>;

/** Prefix used for Inbox tasks (no area). Reserved; areas/spaces may not use it. */
export const INBOX_PREFIX = "DSK";

// ---------- icon assets ----------
/**
 * An imported SVG icon (added v0.4), stored in Desk itself. The server sanitizes it,
 * pads it into a square without changing its proportions or colors, and resolves
 * theme-dependent color (`currentColor`, `prefers-color-scheme`) per app theme.
 * `plate` is set when the mark's own colors fall below 3:1 against that theme's
 * surfaces; clients then draw it on a neutral tile of that tone so it stays visible.
 */
export const IconPlate = z.enum(["none", "light", "dark"]);
export type IconPlate = z.infer<typeof IconPlate>;
export const IconVariant = z.object({ svg: z.string(), plate: IconPlate });
/** `auto` adds the contrast tile where needed; `never` always keeps the background transparent. Added v0.5. */
export const IconTile = z.enum(["auto", "never"]);
export const IconAsset = z.object({
  id: Uuid,
  name: z.string().min(1).max(120),
  /** SHA-256 of the uploaded file; uploading identical bytes returns the existing asset. */
  sha256: z.string().regex(/^[0-9a-f]{64}$/),
  tile: IconTile,
  light: IconVariant,
  dark: IconVariant,
  createdAt: IsoDateTime,
});
export type IconAsset = z.infer<typeof IconAsset>;
export const CreateIconAssetInput = z.object({
  name: z.string().trim().min(1).max(120),
  /** Raw SVG markup, up to 256 KB. */
  svg: z.string().min(1).max(262_144),
  /** Re-sending identical bytes with a different value updates that asset's setting. */
  tile: IconTile.optional(),
});

// ---------- media ----------
/**
 * Stored image file (added v0.6), e.g. a space's header wordmark. Metadata only; the
 * bytes are served by `GET /v1/media/:id`. SVGs pass the icon sanitizer on upload.
 */
export const MediaMime = z.enum(["image/svg+xml", "image/png", "image/webp", "image/jpeg"]);
export type MediaMime = z.infer<typeof MediaMime>;
export const Media = z.object({
  id: Uuid,
  name: z.string().min(1).max(120),
  mime: MediaMime,
  /** SHA-256 of the stored bytes; uploading identical bytes returns the existing record. */
  sha256: z.string().regex(/^[0-9a-f]{64}$/),
  width: z.number().nullable(),
  height: z.number().nullable(),
  /** Stored size in bytes. */
  bytes: z.number().int().nonnegative(),
  createdAt: IsoDateTime,
});
export type Media = z.infer<typeof Media>;
/** Up to 16 MB per request (base64 included). */
export const CreateMediaInput = z.object({
  name: z.string().trim().min(1).max(120),
  mime: MediaMime,
  base64: z.string().min(1).regex(/^[A-Za-z0-9+/]+={0,2}$/, "Expected base64"),
});
export const MediaList = z.object({ items: z.array(Media) });

// ---------- container details (added v0.6) ----------
export const HexColor = z.string().regex(/^#[0-9A-Fa-f]{6}$/, "Expected #RRGGBB");
/** http(s) links open in the browser; file:// links are shown as copyable paths. */
export const ContainerLink = z.object({
  label: z.string().trim().min(1).max(60),
  url: z
    .string()
    .trim()
    .max(2000)
    .regex(/^(?:https?:\/\/[^\s/?#]+(?:[/?#]\S*)?|file:\/\/(?:localhost)?\/\S*)$/i, "Links must be http(s) or file:// URLs (spaces percent-encoded)"),
});
export type ContainerLink = z.infer<typeof ContainerLink>;
export const ContainerLinks = z.array(ContainerLink).max(10);
export const ContainerDescription = z.string().max(2000);
const Details = {
  description: ContainerDescription,
  color: HexColor.nullable(),
  links: ContainerLinks,
  /** Header image (Media id), e.g. a wordmark shown in place of the title. */
  headerMediaId: Uuid.nullable(),
};
const DetailsInput = {
  description: ContainerDescription.optional(),
  color: HexColor.nullable().optional(),
  links: ContainerLinks.optional(),
  headerMediaId: Uuid.nullable().optional(),
};

/**
 * Preset icon colors for area/space symbols (added v0.8). Each has light and dark tones
 * in @desk/design ICON_COLORS and the iOS IconPalette.
 */
export const IconColorName = z.enum(["ink", "slate", "crimson", "ember", "ochre", "lime", "moss", "teal", "blue", "indigo", "plum", "rose"]);
export type IconColorName = z.infer<typeof IconColorName>;
/** SF Symbol name of a preset symbol (web maps it to a lucide glyph). */
const SymbolKey = z.string().max(40);
const IconStyle = {
  icon: SymbolKey.nullable(),
  /** Tint for `icon`; null means ink. */
  iconColor: IconColorName.nullable(),
};
const IconStyleInput = {
  icon: SymbolKey.nullable().optional(),
  iconColor: IconColorName.nullable().optional(),
};

// ---------- containers ----------
const Attention = {
  /** Sidebar settle (spaces/projects). Never changes task state. */
  settledAt: IsoDateTime.nullable(),
  /** Timed sidebar settle. Container returns to the active sidebar at this time. */
  snoozedUntil: IsoDateTime.nullable(),
};
const Audit = {
  createdAt: IsoDateTime,
  updatedAt: IsoDateTime,
  archivedAt: IsoDateTime.nullable(),
  revision: z.number().int().positive(),
};

export const Area = z.object({
  id: Uuid,
  name: z.string().min(1).max(120),
  prefix: Prefix,
  /** Added v0.4. */
  iconAssetId: Uuid.nullable(),
  /** Added v0.8. A preset symbol shown when there's no `iconAssetId`. */
  ...IconStyle,
  /** Added v0.6. */
  ...Details,
  position: z.number(),
  ...Audit,
});
export type Area = z.infer<typeof Area>;

export const Space = z.object({
  id: Uuid,
  /** null for a top-level space (added v0.7). */
  areaId: Uuid.nullable(),
  name: z.string().min(1).max(120),
  prefix: Prefix,
  /** SF Symbol name of a preset symbol; `iconColor` tints it (added v0.8). */
  icon: SymbolKey.nullable(),
  iconColor: IconColorName.nullable(),
  /** Imported icon (added v0.4). Takes precedence over `icon` when set. */
  iconAssetId: Uuid.nullable(),
  /** Added v0.6. */
  ...Details,
  position: z.number(),
  ...Attention,
  ...Audit,
});
export type Space = z.infer<typeof Space>;

export const Project = z.object({
  id: Uuid,
  /** The space's area; null when the space is top-level (v0.7). */
  areaId: Uuid.nullable(),
  spaceId: Uuid,
  name: z.string().min(1).max(160),
  state: ProjectState,
  deadline: LocalDate.nullable(),
  /** Added v0.9. A project's own color; it never inherits its space's. */
  description: ContainerDescription,
  color: HexColor.nullable(),
  links: ContainerLinks,
  position: z.number(),
  completedAt: IsoDateTime.nullable(),
  ...Attention,
  ...Audit,
});
export type Project = z.infer<typeof Project>;

// ---------- tasks ----------
export const ChecklistItem = z.object({
  id: Uuid,
  title: z.string().min(1).max(500),
  done: z.boolean(),
  position: z.number(),
});
export type ChecklistItem = z.infer<typeof ChecklistItem>;

/**
 * Repeat rule (added v0.3). Completing or cancelling a repeating task creates the
 * next occurrence as a new task with its own readable id.
 * - `weekdays` repeats Monday-Friday and ignores `interval`.
 * - `from: "schedule"` keeps a fixed calendar cadence (every Monday, the 1st of the
 *   month); `from: "completion"` counts from the day the previous one was finished.
 */
export const RepeatFrequency = z.enum(["daily", "weekdays", "weekly", "monthly", "yearly"]);
export type RepeatFrequency = z.infer<typeof RepeatFrequency>;
export const RepeatRule = z.object({
  frequency: RepeatFrequency,
  interval: z.number().int().min(1).max(99),
  from: z.enum(["schedule", "completion"]),
});
export type RepeatRule = z.infer<typeof RepeatRule>;

export const Task = z.object({
  id: Uuid,
  /** e.g. HUM-12. Assigned by the server; stable across moves. */
  readableId: ReadableId,
  title: z.string().min(1).max(500),
  description: z.string().max(20000),
  /** null for Inbox tasks and tasks in top-level spaces. Inbox = no area and no space (v0.7). */
  areaId: Uuid.nullable(),
  spaceId: Uuid.nullable(),
  projectId: Uuid.nullable(),
  status: TaskStatus,
  priority: Priority,
  /** Latest active day-plan date ("When"), derived from plan entries. Not a deadline. */
  plannedDate: LocalDate.nullable(),
  /** Real deadline (day-only). */
  deadline: LocalDate.nullable(),
  /** Explicit reminder instant. */
  reminderAt: IsoDateTime.nullable(),
  snoozedUntil: IsoDateTime.nullable(),
  labels: z.array(z.string().min(1).max(40)),
  checklist: z.array(ChecklistItem),
  /** Manual order within its container. */
  position: z.number(),
  createdAt: IsoDateTime,
  updatedAt: IsoDateTime,
  completedAt: IsoDateTime.nullable(),
  /** Last meaningful user activity (edit, status change, plan, review). Drives follow-up. */
  lastActivityAt: IsoDateTime,
  /** Soft delete; recoverable via restore. */
  deletedAt: IsoDateTime.nullable(),
  revision: z.number().int().positive(),
  /** Added v0.3. */
  repeat: RepeatRule.nullable(),
  /** Added v0.3. The occurrence created when this one was finished; set once, so re-completing never duplicates. */
  nextOccurrenceId: Uuid.nullable(),
});
export type Task = z.infer<typeof Task>;

/** Computed, read-only attention flags returned alongside tasks in list endpoints. */
export const TaskFlags = z.object({
  isSnoozed: z.boolean(),
  isOverdue: z.boolean(),
  isDueToday: z.boolean(),
  /** Container (space/project) is settled or sidebar-snoozed. Informational only. */
  inSettledContainer: z.boolean(),
});
export type TaskFlags = z.infer<typeof TaskFlags>;
export const TaskWithFlags = Task.extend({ flags: TaskFlags });
export type TaskWithFlags = z.infer<typeof TaskWithFlags>;

// ---------- inputs ----------
export const CreateAreaInput = z.object({
  name: z.string().trim().min(1).max(120),
  prefix: Prefix.optional(),
  iconAssetId: Uuid.nullable().optional(),
  ...IconStyleInput,
  ...DetailsInput,
  clientId: Uuid.optional(),
});
export const UpdateAreaInput = z.object({
  name: z.string().trim().min(1).max(120).optional(),
  prefix: Prefix.optional(),
  iconAssetId: Uuid.nullable().optional(),
  ...IconStyleInput,
  ...DetailsInput,
  position: z.number().optional(),
  baseRevision: z.number().int().optional(),
});
export const CreateSpaceInput = z.object({
  /** Omit or null for a top-level space (v0.7). */
  areaId: Uuid.nullable().optional(),
  name: z.string().trim().min(1).max(120),
  prefix: Prefix.optional(),
  ...IconStyleInput,
  iconAssetId: Uuid.nullable().optional(),
  ...DetailsInput,
  clientId: Uuid.optional(),
});
export const UpdateSpaceInput = z.object({
  name: z.string().trim().min(1).max(120).optional(),
  /** Move the space into an area, or null to make it top-level; its projects and tasks follow (v0.7). */
  areaId: Uuid.nullable().optional(),
  ...IconStyleInput,
  iconAssetId: Uuid.nullable().optional(),
  ...DetailsInput,
  position: z.number().optional(),
  baseRevision: z.number().int().optional(),
});
export const CreateProjectInput = z.object({
  spaceId: Uuid,
  name: z.string().trim().min(1).max(160),
  state: ProjectState.optional(),
  deadline: LocalDate.nullable().optional(),
  description: ContainerDescription.optional(),
  color: HexColor.nullable().optional(),
  links: ContainerLinks.optional(),
  clientId: Uuid.optional(),
});
export const UpdateProjectInput = z.object({
  name: z.string().trim().min(1).max(160).optional(),
  state: z.enum(["planned", "active"]).optional(),
  deadline: LocalDate.nullable().optional(),
  description: ContainerDescription.optional(),
  color: HexColor.nullable().optional(),
  links: ContainerLinks.optional(),
  position: z.number().optional(),
  baseRevision: z.number().int().optional(),
});
/** Completing a project with open tasks requires an explicit choice. */
export const CompleteProjectInput = z.object({
  openTasks: z.enum(["complete", "move_to_space", "keep"]),
  baseRevision: z.number().int().optional(),
});

/** Destination: provide the most specific id. Server derives and validates the parents. */
export const Destination = z.object({
  areaId: Uuid.nullable().optional(),
  spaceId: Uuid.nullable().optional(),
  projectId: Uuid.nullable().optional(),
});
export type Destination = z.infer<typeof Destination>;

export const CreateTaskInput = Destination.extend({
  title: z.string().trim().min(1).max(500),
  description: z.string().max(20000).optional(),
  status: TaskStatus.optional(), // default "todo"
  priority: Priority.optional(), // default "none"
  deadline: LocalDate.nullable().optional(),
  reminderAt: IsoDateTime.nullable().optional(),
  labels: z.array(z.string().trim().min(1).max(40)).optional(),
  checklist: z.array(z.string().trim().min(1).max(500)).optional(),
  /** Add to this day's plan on create (explicit user choice only). */
  planDate: LocalDate.optional(),
  repeat: RepeatRule.nullable().optional(),
  clientId: Uuid.optional(),
});
export type CreateTaskInput = z.infer<typeof CreateTaskInput>;

export const UpdateTaskInput = z.object({
  title: z.string().trim().min(1).max(500).optional(),
  description: z.string().max(20000).optional(),
  status: TaskStatus.optional(),
  priority: Priority.optional(),
  deadline: LocalDate.nullable().optional(),
  reminderAt: IsoDateTime.nullable().optional(),
  labels: z.array(z.string().trim().min(1).max(40)).optional(),
  position: z.number().optional(),
  /** null stops repeating. */
  repeat: RepeatRule.nullable().optional(),
  baseRevision: z.number().int().optional(),
});
export type UpdateTaskInput = z.infer<typeof UpdateTaskInput>;

export const MoveTaskInput = Destination.extend({ baseRevision: z.number().int().optional() });
export const SnoozeInput = z.object({ until: IsoDateTime });
/** POST /v1/tasks/:id/snooze. `baseRevision` added v0.2 (additive). */
export const TaskSnoozeInput = SnoozeInput.extend({ baseRevision: z.number().int().optional() });
/** Optional body for task-scoped actions without other input (unsnooze, checklist delete). Added v0.2. */
export const RevisionInput = z.object({ baseRevision: z.number().int().optional() });
export const ChecklistAddInput = z.object({
  title: z.string().trim().min(1).max(500),
  clientId: Uuid.optional(),
  /** Task revision (added v0.2). A replay of an existing clientId is never rejected. */
  baseRevision: z.number().int().optional(),
});
export const ChecklistUpdateInput = z.object({
  title: z.string().trim().min(1).max(500).optional(),
  done: z.boolean().optional(),
  /** Task revision (added v0.2). */
  baseRevision: z.number().int().optional(),
});
export const ReorderInput = z.object({ ids: z.array(Uuid).min(1) });
/** POST /v1/tasks/:id/checklist/reorder (added v0.2: optional task `baseRevision`). */
export const ChecklistReorderInput = ReorderInput.extend({ baseRevision: z.number().int().optional() });

// ---------- task queries ----------
export const TaskQuery = z.object({
  areaId: Uuid.optional(),
  spaceId: Uuid.optional(),
  projectId: Uuid.optional(),
  /** "inbox" = tasks without an area. */
  scope: z.enum(["all", "inbox"]).optional(),
  /** Only tasks filed directly in the given container (not descendants). */
  direct: z.coerce.boolean().optional(),
  /** "none" = tasks with no project inside the given space/area. */
  noProject: z.coerce.boolean().optional(),
  status: z.union([TaskStatus, z.array(TaskStatus)]).optional(),
  priority: z.union([Priority, z.array(Priority)]).optional(),
  label: z.string().optional(),
  q: z.string().optional(),
  /** Done/cancelled hidden unless included or status filter asks for them. */
  includeClosed: z.coerce.boolean().optional(),
  /** Snoozed tasks are included in explicit container views with a flag; hidden in attention lists. */
  includeSnoozed: z.coerce.boolean().optional(),
  includeDeleted: z.coerce.boolean().optional(),
  /** Tasks inside archived containers are hidden from unscoped lists unless included (added v0.1). */
  includeArchived: z.coerce.boolean().optional(),
  /** IANA timezone for flags (isOverdue/isDueToday). Defaults to settings.timezone (added v0.1). */
  tz: z.string().optional(),
  sort: z.enum(["priority", "deadline", "created", "manual", "updated"]).optional(),
  limit: z.coerce.number().int().min(1).max(500).optional(),
  cursor: z.string().optional(),
});
export type TaskQuery = z.infer<typeof TaskQuery>;
export const TaskPage = z.object({ items: z.array(TaskWithFlags), nextCursor: z.string().nullable() });
export type TaskPage = z.infer<typeof TaskPage>;

// ---------- Today / planning ----------
export const PlanMode = z.enum(["priority", "manual"]);
export const PlanEntry = z.object({
  taskId: Uuid,
  date: LocalDate,
  position: z.number(),
  createdAt: IsoDateTime,
});
export const TodayRow = z.object({
  task: TaskWithFlags,
  /** For carryover rows: the original plan date. */
  carriedFrom: LocalDate.nullable(),
});
export const TodayView = z.object({
  date: LocalDate,
  timezone: z.string(),
  mode: PlanMode,
  /** Chosen tasks for this date, open only, ordered per mode. Due state shown via flags. */
  planned: z.array(TodayRow),
  /** Unfinished tasks planned on an earlier date and not yet kept/rescheduled/removed. */
  carryover: z.array(TodayRow),
  /** Open tasks due today or overdue that are NOT in planned/carryover (includes snoozed). */
  due: z.array(TodayRow),
  /** Tasks from this date's plan completed today. */
  completed: z.array(TodayRow),
});
export type TodayView = z.infer<typeof TodayView>;
export const PlanAddInput = z.object({ date: LocalDate, taskIds: z.array(Uuid).min(1) });
export const PlanRemoveInput = z.object({ date: LocalDate, taskId: Uuid });
export const PlanReorderInput = z.object({ date: LocalDate, taskIds: z.array(Uuid).min(1) });
export const PlanModeInput = z.object({ date: LocalDate, mode: PlanMode });
export const CarryoverInput = z.object({
  date: LocalDate,
  taskId: Uuid,
  action: z.enum(["keep", "reschedule", "remove"]),
  toDate: LocalDate.optional(),
});

export const UpcomingDay = z.object({
  date: LocalDate,
  planned: z.array(TaskWithFlags),
  deadlines: z.array(TaskWithFlags),
});
export const UpcomingView = z.object({ from: LocalDate, days: z.array(UpcomingDay) });

export const ReviewReason = z.enum(["urgent_undated", "stale_high", "stale_medium", "stale_low"]);
export const ReviewItem = z.object({ task: TaskWithFlags, reason: ReviewReason, staleSince: IsoDateTime });
export const ReviewView = z.object({ items: z.array(ReviewItem) });

// ---------- settings / notifications ----------
export const Settings = z.object({
  timezone: z.string(),
  followUp: z.object({
    enabled: z.boolean(),
    urgentNotify: z.boolean(), // one follow-up at next notifyHour for undated urgent
    highDays: z.number().int().min(1).max(90),
    mediumDays: z.number().int().min(1).max(90),
    lowDays: z.number().int().min(1).max(180),
    notifyHour: z.number().int().min(0).max(23),
  }),
  quietHours: z.object({ enabled: z.boolean(), start: z.string(), end: z.string() }), // "22:00" / "08:00"
  /** Device id that should present notifications; null = any. */
  preferredNotificationDevice: z.string().nullable(),
  /**
   * Automatic notice categories (added v0.10). Explicit reminders always fire; the urgent
   * follow-up keeps its own `followUp.urgentNotify` switch.
   */
  notificationKinds: z.object({ deadline: z.boolean(), snoozeWake: z.boolean() }),
  revision: z.number().int().positive(),
});
export type Settings = z.infer<typeof Settings>;
export const DEFAULT_NOTIFICATION_KINDS = { deadline: true, snoozeWake: true } as const;
export const UpdateSettingsInput = z.object({
  timezone: z.string().optional(),
  followUp: Settings.shape.followUp.partial().optional(),
  quietHours: Settings.shape.quietHours.partial().optional(),
  preferredNotificationDevice: z.string().nullable().optional(),
  notificationKinds: Settings.shape.notificationKinds.partial().optional(),
  baseRevision: z.number().int().optional(),
});

// ---------- MCP access (owner token only, added v0.10) ----------
/**
 * State of the scoped MCP credential. The token itself is never returned: the MCP server reads it
 * from credentials.json in `dataDir`, so the setup config carries no secret.
 */
export const McpAccess = z.object({
  enabled: z.boolean(),
  /** Last rotation; null for the token created on first start. */
  rotatedAt: IsoDateTime.nullable(),
  /** Data directory the MCP server must read (`DESK_DATA_DIR`); null when the API has none. */
  dataDir: z.string().nullable(),
  /** Absolute path of `desk-mcp.mjs` when the API runs from the repo; null otherwise. */
  serverPath: z.string().nullable(),
  /** Whether `serverPath` exists (built with `pnpm mcp:build`). */
  serverBuilt: z.boolean(),
});
export type McpAccess = z.infer<typeof McpAccess>;
export const UpdateMcpAccessInput = z.object({ enabled: z.boolean() });

export const NotificationKind = z.enum(["reminder", "snooze_wake", "follow_up", "deadline"]);
export const ScheduledNotification = z.object({
  id: z.string(), // dedupe key: `${taskId}:${kind}:${fireAt}`
  taskId: Uuid,
  readableId: ReadableId,
  kind: NotificationKind,
  title: z.string(),
  body: z.string(),
  fireAt: IsoDateTime,
});
export const NotificationSchedule = z.object({ items: z.array(ScheduledNotification) });

// ---------- bootstrap / sync ----------
export const Bootstrap = z.object({
  areas: z.array(Area),
  spaces: z.array(Space),
  projects: z.array(Project),
  /** Icons referenced by areas and spaces (added v0.4). */
  assets: z.array(IconAsset),
  /** Media metadata (no bytes), added v0.6. */
  media: z.array(Media),
  settings: Settings,
  serverTime: IsoDateTime,
  /** Opaque change cursor; pass to /v1/changes?since= */
  cursor: z.string(),
});
export type Bootstrap = z.infer<typeof Bootstrap>;

export const ChangeEvent = z.object({
  seq: z.number().int(),
  entity: z.enum(["area", "space", "project", "task", "plan", "settings"]),
  id: z.string(),
  op: z.enum(["upsert", "delete"]),
  at: IsoDateTime,
});
export const ChangesPage = z.object({ events: z.array(ChangeEvent), cursor: z.string() });

// Exports written before v0.8 have no area icon or icon color.
const iconDefaults = { icon: SymbolKey.nullable().default(null), iconColor: IconColorName.nullable().default(null) };
const detailDefaults = {
  description: ContainerDescription.default(""),
  color: HexColor.nullable().default(null),
  links: ContainerLinks.default([]),
  headerMediaId: Uuid.nullable().default(null),
};
export const ExportFile = z.object({
  format: z.literal("desk-export"),
  version: z.literal(1),
  exportedAt: IsoDateTime,
  // Exports written before v0.4 have no icon fields, and before v0.6 no details.
  areas: z.array(Area.extend({ iconAssetId: Uuid.nullable().default(null), ...iconDefaults, ...detailDefaults })),
  spaces: z.array(Space.extend({ iconAssetId: Uuid.nullable().default(null), ...iconDefaults, ...detailDefaults })),
  // Exports written before v0.9 have no project details.
  projects: z.array(
    Project.extend({
      description: ContainerDescription.default(""),
      color: HexColor.nullable().default(null),
      links: ContainerLinks.default([]),
    }),
  ),
  assets: z.array(IconAsset.extend({ source: z.string(), tile: IconTile.default("auto") })).default([]),
  /** Media with its bytes as base64 (added v0.6). */
  media: z.array(Media.extend({ base64: z.string() })).default([]),
  // Exports written before v0.3 have no repeat fields.
  tasks: z.array(
    Task.extend({
      repeat: RepeatRule.nullable().default(null),
      nextOccurrenceId: Uuid.nullable().default(null),
    }),
  ),
  planEntries: z.array(PlanEntry.extend({ removedAt: IsoDateTime.nullable() })),
  // Exports written before v0.10 have no notification categories.
  settings: Settings.extend({
    notificationKinds: Settings.shape.notificationKinds.default({ ...DEFAULT_NOTIFICATION_KINDS }),
  }),
});
export const ImportInput = z.object({
  file: ExportFile,
  /** merge: add records whose ids don't exist; never overwrite. dryRun reports counts only. */
  mode: z.enum(["merge"]),
  dryRun: z.boolean().optional(),
});

export const SearchResult = z.object({
  tasks: z.array(TaskWithFlags),
  areas: z.array(Area),
  spaces: z.array(Space),
  projects: z.array(Project),
});

// ---------- errors ----------
export const ApiErrorCode = z.enum([
  "unauthorized",
  "forbidden",
  "not_found",
  "validation_failed",
  "invalid_parent",
  "conflict", // revision mismatch; `current` holds the latest record
  "ambiguous", // name lookup matched several records; `candidates` lists them
  "prefix_taken",
  "rate_limited",
  "internal",
]);
export const ApiError = z.object({
  error: z.object({
    code: ApiErrorCode,
    message: z.string(),
    details: z.unknown().optional(),
    current: z.unknown().optional(),
    candidates: z.array(z.object({ id: z.string(), name: z.string(), kind: z.string() })).optional(),
  }),
});
export type ApiError = z.infer<typeof ApiError>;

// ---------- route table (documentation + client generation) ----------
export const ROUTES = {
  health: "GET /health",
  bootstrap: "GET /v1/bootstrap",
  /** Added v0.1: single-record container reads and server-computed snooze presets. */
  containers: ["GET /v1/areas/:id", "GET /v1/spaces/:id", "GET /v1/projects/:id"],
  snoozePresets: "GET /v1/snooze/presets?tz=",
  changes: "GET /v1/changes?since=",
  events: "GET /v1/events (SSE; token via Authorization or ?token= for EventSource)",
  areas: ["GET /v1/areas", "POST /v1/areas", "PATCH /v1/areas/:id", "POST /v1/areas/:id/archive", "POST /v1/areas/:id/restore"],
  spaces: [
    "GET /v1/spaces?areaId=", "POST /v1/spaces", "PATCH /v1/spaces/:id",
    "POST /v1/spaces/:id/settle", "POST /v1/spaces/:id/unsettle",
    "POST /v1/spaces/:id/snooze", "POST /v1/spaces/:id/unsnooze",
    "POST /v1/spaces/:id/archive", "POST /v1/spaces/:id/restore",
  ],
  projects: [
    "GET /v1/projects?spaceId=", "POST /v1/projects", "PATCH /v1/projects/:id",
    "POST /v1/projects/:id/complete", "POST /v1/projects/:id/reopen",
    "POST /v1/projects/:id/settle", "POST /v1/projects/:id/unsettle",
    "POST /v1/projects/:id/snooze", "POST /v1/projects/:id/unsnooze",
    "POST /v1/projects/:id/archive", "POST /v1/projects/:id/restore",
  ],
  tasks: [
    "GET /v1/tasks", "GET /v1/tasks/:idOrReadableId", "POST /v1/tasks", "PATCH /v1/tasks/:id",
    "POST /v1/tasks/:id/move", "POST /v1/tasks/:id/complete", "POST /v1/tasks/:id/reopen",
    "POST /v1/tasks/:id/cancel", "POST /v1/tasks/:id/snooze", "POST /v1/tasks/:id/unsnooze",
    "POST /v1/tasks/:id/review", "DELETE /v1/tasks/:id", "POST /v1/tasks/:id/restore",
    "POST /v1/tasks/reorder",
    "POST /v1/tasks/:id/checklist", "PATCH /v1/tasks/:id/checklist/:itemId",
    "DELETE /v1/tasks/:id/checklist/:itemId", "POST /v1/tasks/:id/checklist/reorder",
  ],
  planning: [
    "GET /v1/today?date=&tz=", "POST /v1/plan", "POST /v1/plan/remove", "POST /v1/plan/reorder",
    "PUT /v1/plan/mode", "POST /v1/plan/carryover", "GET /v1/upcoming?from=&days=",
    "GET /v1/review", "GET /v1/snoozed", "GET /v1/settled",
  ],
  search: "GET /v1/search?q=",
  resolve: "GET /v1/resolve?kind=area|space|project&name= (exact/prefix match; ambiguous -> 409 ambiguous)",
  settings: ["GET /v1/settings", "PATCH /v1/settings"],
  notifications: ["GET /v1/notifications/schedule?device=&horizonHours=", "POST /v1/notifications/:id/ack"],
  data: ["GET /v1/export", "POST /v1/import"],
  /** Added v0.10. Owner token only. Rotating or disabling applies to the running server at once. */
  mcp: ["GET /v1/mcp", "PATCH /v1/mcp", "POST /v1/mcp/rotate"],
  /** Added v0.4. Owner token only. */
  assets: ["GET /v1/assets", "POST /v1/assets"],
  /** Added v0.6. POST is owner token only; GET /v1/media/:id returns the bytes. */
  media: ["GET /v1/media", "POST /v1/media", "GET /v1/media/:id"],
} as const;

// ---------- response shapes added in v0.1 (additive; see CHANGES.md) ----------
export const Health = z.object({ ok: z.literal(true), version: z.string() });

/** GET /v1/areas, /v1/spaces, /v1/projects respond with `{ items }`. */
export const AreaList = z.object({ items: z.array(Area) });
export const SpaceList = z.object({ items: z.array(Space) });
export const ProjectList = z.object({ items: z.array(Project) });
export const IconAssetList = z.object({ items: z.array(IconAsset) });

/** GET /v1/snoozed: snoozed tasks (soonest wake first) and sidebar-snoozed containers. */
export const SnoozedView = z.object({
  tasks: z.array(TaskWithFlags),
  spaces: z.array(Space),
  projects: z.array(Project),
});
export type SnoozedView = z.infer<typeof SnoozedView>;

/** GET /v1/settled: settled or sidebar-snoozed spaces/projects. */
export const SettledView = z.object({ spaces: z.array(Space), projects: z.array(Project) });
export type SettledView = z.infer<typeof SettledView>;

export const ResolveKind = z.enum(["area", "space", "project"]);
export type ResolveKind = z.infer<typeof ResolveKind>;
/** GET /v1/resolve query. `areaId`/`spaceId` narrow the search scope. */
export const ResolveQuery = z.object({
  name: z.string().trim().min(1),
  kind: ResolveKind.optional(),
  areaId: Uuid.optional(),
  spaceId: Uuid.optional(),
  includeArchived: z.coerce.boolean().optional(),
});
export const ResolveMatch = z.object({
  kind: ResolveKind,
  id: Uuid,
  name: z.string(),
  /** Area/space prefix; null for projects. */
  prefix: Prefix.nullable(),
  /** null for a top-level space and its projects (v0.7). */
  areaId: Uuid.nullable(),
  spaceId: Uuid.nullable(),
  /** Human-readable location, e.g. "Aevix Technologies / HumVision / Windows release". */
  path: z.string(),
});
export type ResolveMatch = z.infer<typeof ResolveMatch>;
export const ResolveResult = z.object({ match: ResolveMatch });

/** GET /v1/snooze/presets?tz= — same values as the exported `snoozePresets()` helper in apps/api. */
export const SnoozePresets = z.object({
  timezone: z.string(),
  now: IsoDateTime,
  in1h: IsoDateTime,
  in3h: IsoDateTime,
  tomorrow9: IsoDateTime,
  nextMonday9: IsoDateTime,
});
export type SnoozePresets = z.infer<typeof SnoozePresets>;

const ImportCounts = z.object({
  areas: z.number().int(),
  /** Added v0.6. */
  media: z.number().int().optional(),
  spaces: z.number().int(),
  projects: z.number().int(),
  tasks: z.number().int(),
  planEntries: z.number().int(),
});
/** POST /v1/import response. Existing ids are skipped, never overwritten. */
export const ImportResult = z.object({
  dryRun: z.boolean(),
  created: ImportCounts,
  skipped: ImportCounts,
  /** Imported tasks whose readable id was already used by a different task. */
  renumbered: z.array(z.object({ id: Uuid, from: z.string(), to: z.string() })),
  /** Imported areas/spaces whose prefix was already taken. */
  reprefixed: z.array(z.object({ id: Uuid, kind: z.enum(["area", "space"]), from: z.string(), to: z.string() })),
});
export type ImportResult = z.infer<typeof ImportResult>;

export const NotificationAck = z.object({ id: z.string(), ackedAt: IsoDateTime });

// ---------- Desk cloud (v0.11) ----------
/** GET /v1/cloud on the Mac: sync with the person's cloud copy. */
export const CloudState = z.enum(["signed_out", "needs_choice", "idle", "syncing", "offline", "error", "update_required"]);
export const CloudStatus = z.object({
  state: CloudState,
  email: z.string().nullable(),
  apiUrl: z.string(),
  lastSyncAt: IsoDateTime.nullable(),
  lastError: z.string().nullable(),
  /** Rows changed on this Mac and not yet pushed. */
  pending: z.number().int(),
});
export type CloudStatus = z.infer<typeof CloudStatus>;

export const SharedMember = z.object({
  email: z.string(),
  role: z.enum(["owner", "member"]),
  status: z.enum(["invited", "active"]),
});
/** GET /v1/sharing: areas shared with or by me, and invitations waiting for me. */
export const SharingState = z.object({
  areas: z.array(
    z.object({
      areaId: Uuid,
      name: z.string(),
      role: z.enum(["owner", "member"]),
      owner: z.string(),
      members: z.array(SharedMember),
    }),
  ),
  invitations: z.array(z.object({ hubId: Uuid, name: z.string(), invitedBy: z.string() })),
});
export type SharingState = z.infer<typeof SharingState>;
